import React, { useCallback } from "react";
import debounce from "debounce-promise";
import _ from "lodash";
import { compose } from "recompose";
import { connect } from "react-redux";
import { Map } from "immutable";
import { MessageDescriptor } from "react-intl";

import { AsyncSelect, AsyncSelectProps } from "@ui/AsyncSelect";
import { NormalisedEntities } from "../../models";
import { ActionCreator, connectToApi, ApiInjectedProps, UpdateActionProps } from "@api/components/connectToApi";

interface OuterProps<M> {
    selected: number | number[];
    multiple?: boolean;
    placeholder?: string;
    placeholderMessage?: MessageDescriptor;
    onChange?: (m: M | M[]) => void;
    id?: string;
}

interface ModelSelectStateProps<M> {
    selectedModels: M[];
}

type Props<M> = ModelSelectStateProps<M> & UpdateActionProps & ApiInjectedProps;

interface SelectModelComponents<M> {
    presenter: React.ComponentType<Props<M> & OuterProps<M>>;
    connected: React.ComponentType<OuterProps<M>>;
}

const makeModelSelectComponent = <M, S>(
    className: string,
    getOptionValue: (model: M) => string,
    getOptionLabel: (model: M) => string,
    actionCreator: ActionCreator,
    makeHydrateFn: (entities: NormalisedEntities) => (id: number) => M,
    makeMapStateToProps: () => (state: S, props: OuterProps<M>) => ModelSelectStateProps<M>,
): SelectModelComponents<M> => {
    type SelectModel = typeof AsyncSelect<M>;
    const SelectModel = AsyncSelect as SelectModel;

    const presenter = ({
        multiple,
        selectedModels,
        placeholder,
        placeholderMessage,
        onChange,
        updateActionProps,
        call,
        id,
    }: Props<M> & OuterProps<M>) => {
        const debouncePerformRequest = useCallback(
            debounce(async (q: string) => {
                updateActionProps([[ q ]]);

                let res = await call(0);

                // Convert the top level to an object
                if (Map.isMap(res)) {
                    const map = res as Map<string, any>;
                    res = map.toObject();
                }
                // And the entities
                if (Map.isMap(res.entities)) {
                    const map = res.entities as Map<string, any>;
                    res.entities = map.toObject();
                }
                const hydrateFn = makeHydrateFn(res.entities);
                return res.result.map((modelId: number) => hydrateFn(modelId));
            }, 200),
            [],
        );
        const handleChange = (option: M | M[]) => {
            if (onChange != null) {
                if (Array.isArray(option)) {
                    onChange(option);
                }
                else if (option != null) {
                    onChange(option);
                }
                else {
                    onChange(null);
                }
            }
        };
        const props: AsyncSelectProps<M> = {
            getOptionValue,
            getOptionLabel,
            value: selectedModels,
            isMulti: multiple != null && multiple === true,
            onChange: handleChange,
            defaultOptions: false,
            cacheOptions: false,
            loadOptions: debouncePerformRequest,
            inputId: id,
        };
        if (placeholder != null) {
            props.placeholder = placeholder;
        }
        if (placeholderMessage != null) {
            props.placeholderMessage = placeholderMessage;
        }
        return (
            <div className={className}>
                <SelectModel {...props} />
            </div>
        );
    };

    return {
        presenter,
        connected: compose<Props<M>, OuterProps<M>>(
            connectToApi({
                loadOnMount: false,
                showRefresh: false,
                actionCreator,
            }),
            connect(makeMapStateToProps, null),
        )(presenter),
    };
};

export {
    makeModelSelectComponent,
    OuterProps as ModelSelectOuterProps,
    ModelSelectStateProps,
    Props as ModelSelectProps,
};
